import { WarningCircle } from '@phosphor-icons/react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export interface ErrorStateProps {
  title?: string
  description?: string
  error?: Error | string | null
  onRetry?: () => void
  onBack?: () => void
  retryLabel?: string
  backLabel?: string
  fullScreen?: boolean
  className?: string
}

export function ErrorState({
  title = 'Something went wrong',
  description = 'Please try again or contact support if the issue persists.',
  error,
  onRetry,
  onBack,
  retryLabel = 'Try again',
  backLabel = 'Go back',
  fullScreen = false,
  className,
}: ErrorStateProps) {
  const errorMessage = typeof error === 'string' ? error : error?.message

  return (
    <div
      className={cn(
        'w-full flex flex-col items-center justify-center text-center',
        'opacity-0 translate-y-3 animate-in fade-in-0 slide-in-from-bottom-3 duration-500 fill-mode-forwards',
        fullScreen ? 'min-h-[80vh]' : 'py-16',
        className
      )}
    >
      <div className="max-w-md">
        <div
          className={cn(
            'mx-auto flex h-16 w-16 items-center justify-center rounded-full',
            'ring-1 ring-border/50 shadow-sm',
            'bg-destructive/10 text-destructive',
            'opacity-0 scale-90 animate-in fade-in-0 zoom-in-90 duration-500 delay-100 fill-mode-forwards'
          )}
        >
          <WarningCircle className="h-7 w-7" />
        </div>
        <h3
          className={cn(
            'mt-5 text-xl font-semibold tracking-tight',
            'opacity-0 translate-y-1 animate-in fade-in-0 slide-in-from-bottom-1 duration-500 delay-200 fill-mode-forwards'
          )}
        >
          {title}
        </h3>
        <p
          className={cn(
            'mt-3 text-sm leading-relaxed text-muted-foreground/80 max-w-sm mx-auto font-normal',
            'opacity-0 translate-y-1 animate-in fade-in-0 slide-in-from-bottom-1 duration-500 delay-300 fill-mode-forwards'
          )}
        >
          {errorMessage ?? description}
        </p>
        {(onRetry || onBack) && (
          <div
            className={cn(
              'mt-6 flex justify-center gap-3',
              'opacity-0 translate-y-1 animate-in fade-in-0 slide-in-from-bottom-1 duration-500 delay-400 fill-mode-forwards'
            )}
          >
            {onBack && (
              <Button variant="outline" onClick={onBack}>
                {backLabel}
              </Button>
            )}
            {onRetry && (
              <Button onClick={onRetry}>
                {retryLabel}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
